import type { UseQueryOptions } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  BppDataType,
  BppInfiniteQueryOptions,
  BppResponseDataType
} from './types'

const BPP_QUERY_KEY = (params: {
  page: number
  per_page: number
  province_code?: string
  city_code?: string
  search: string
}) => ['reference_bpp', { ...params }] as const

type BppQueryKey = ReturnType<typeof BPP_QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
    province_code: string
    city_code: string
  }>
): BppInfiniteQueryOptions<BppQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? '',
    province_code: opts?.province_code,
    city_code: opts?.city_code
  }

  return {
    queryKey: BPP_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }) => {
      try {
        const res = await api.get<BppResponseDataType>(
          '/report/reference/bpp',
          {
            params: {
              page: pageParam,
              per_page: params.per_page,
              search:
                params.search.length >= 2 ? `%${params.search}%` : undefined,
              province_code: params.province_code,
              city_code: params.city_code
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const BPP_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_bpp', params] as const

const getById = (opts: {
  id: string
}): UseQueryOptions<ServiceGetByIdResponse<BppDataType>> => ({
  queryKey: BPP_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<ServiceGetAllResponse<BppDataType[]>>(
        '/report/reference/bpp',
        {
          params: {
            id: opts.id
          }
        }
      )

      const data = res.data.data?.[0]
      if (!data) {
        throw new Error('BPP not found')
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const bppReference = {
  queryKey: {
    all: BPP_QUERY_KEY,
    byId: BPP_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId: getById
}
